import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { X, Loader2, Send, History } from "lucide-react";
import { API } from "../contexts/AuthContext";
import { toast } from "sonner";

const TIPO_LABEL = { nota: "Nota", llamada: "Llamada", reunion: "Reunión", email: "Email" };

export default function ClienteTrazabilidadModal({ cliente, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nota, setNota] = useState("");
  const [tipo, setTipo] = useState("nota");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/listas/clientes/${cliente._id}/interacciones`);
      setItems(data || []);
    } catch { toast.error("Error al cargar la trazabilidad"); }
    finally { setLoading(false); }
  }, [cliente._id]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!nota.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/listas/clientes/${cliente._id}/interacciones`, { nota: nota.trim(), tipo });
      setNota(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="cliente-trazabilidad-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto flex flex-col">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2"><History size={16} className="text-[#2E6DA4]" /> Trazabilidad — {cliente.cliente}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="cliente-trazabilidad-close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4 flex-1">
          <div className="flex gap-2">
            <select value={tipo} onChange={e => setTipo(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-2 text-xs" data-testid="cliente-trazabilidad-tipo">
              {Object.entries(TIPO_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <input value={nota} onChange={e => setNota(e.target.value)} placeholder="Agregar una nota de seguimiento…" className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="cliente-trazabilidad-input" onKeyDown={e => e.key === "Enter" && submit()} />
            <button onClick={submit} disabled={sending || !nota.trim()} className="px-3 py-2 rounded-lg bg-[#2E6DA4] text-white disabled:opacity-50" data-testid="cliente-trazabilidad-submit">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
          {loading ? (
            <div className="text-center py-6"><Loader2 className="animate-spin mx-auto text-gray-300" /></div>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">Sin interacciones registradas todavía</p>
          ) : (
            <div className="space-y-2">
              {items.map((i) => (
                <div key={i._id} className="border border-gray-100 rounded-xl p-3" data-testid={`cliente-trazabilidad-item-${i._id}`}>
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                    <span className="font-semibold text-[#2E6DA4]">{TIPO_LABEL[i.tipo] || i.tipo}</span>
                    <span>{new Date(i.fecha).toLocaleString("es-PY")} · {i.autor_name}</span>
                  </div>
                  <p className="text-sm text-gray-700">{i.nota}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
